import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SchemaOrg from "@/components/feature/SchemaOrg";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-400"></i>
          <h1 className="text-xl font-bold text-foreground-900 mt-4">文章不存在</h1>
          <p className="text-foreground-500 mt-2">您要查看的文章可能已被移除或链接有误</p>
          <Link
            to="/"
            className="mt-6 inline-block px-5 py-2 bg-rose-500 text-white rounded-full text-sm font-medium hover:bg-rose-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedArticles = articles
    .filter(
      (a) => a.category === article.category && a.slug !== article.slug
    )
    .slice(0, 3);

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.summary,
          image: article.coverImage,
          datePublished: article.date,
          author: {
            "@type": "Organization",
            name: article.author,
          },
          publisher: {
            "@type": "Organization",
            name: "51吃瓜",
            logo: {
              "@type": "ImageObject",
              url: "https://51chigua.com/logo.png",
            },
          },
          url: `https://51chigua.com/article/${slug}`,
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://51chigua.com/article/${slug}`,
          },
        }}
      />

      <main className="min-h-screen pt-16">
        <article>
          <div className="bg-background-50 border-b border-background-200/60">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  {
                    label: article.categoryName,
                    href: `/category/${article.category}`,
                  },
                  { label: article.title },
                ]}
              />

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mt-5 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views > 10000
                    ? `${(article.views / 10000).toFixed(1)}万 阅读`
                    : `${article.views} 阅读`}
                </span>
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-rose-50 text-rose-600 rounded-full text-xs font-medium hover:bg-rose-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {article.categoryName}
                </Link>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/60">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-600 font-medium">
                  标签：
                </span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-700 rounded-full text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-5 bg-background-50 rounded-lg border border-background-200/60">
              <div>
                <h4 className="font-semibold text-foreground-900 text-sm">
                  分享这篇文章
                </h4>
                <p className="text-xs text-foreground-500 mt-0.5">
                  觉得不错？分享给朋友一起看
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-rose-50 hover:text-rose-500 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-rose-50 hover:text-rose-500 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-rose-50 hover:text-rose-500 transition-colors cursor-pointer whitespace-nowrap" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-50 py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group block bg-background-50 rounded-lg border border-background-200/60 overflow-hidden hover:border-rose-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-36 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-rose-500 transition-colors">
                        {ra.title}
                      </h4>
                      <span className="text-xs text-foreground-500 mt-1.5 block">
                        {ra.date}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}